import type { ColorPalette } from '@/features/colors/color.types';
import { toBackgroundIntensity } from '@/features/colors/lib/paletteLookup';
import { Flex, Icon } from '@/ui';
import {
  SidebarCard,
  SidebarIconChip,
  SidebarRow,
  SidebarRowLabel,
  SidebarSection,
} from '../SidebarKit';
import { SidebarRowSwitch } from '../SidebarRowSwitch';
import { useCustomizeControl } from '../useCustomizeControl';
import { IntensitySlider } from './IntensitySlider';

type Props = {
  palette: ColorPalette;
  colorName: string | null | undefined;
  isEnabled: boolean;
};

/**
 * Whether the page canvas is tinted with the accent color, and how far along the
 * accent's ramp that tint goes. Whole-schedule, like the accent color it follows.
 */
export const BackgroundSection = ({ palette, colorName, isEnabled }: Props) => {
  const { register, registerToggle } = useCustomizeControl();
  // The strength rides on `bold` — the legacy Page background's alpha, whose legacy
  // meanings getStyles pins for planners so writing it here moves the canvas alone.
  const { value, onChange, onPreview } = register<'bold', number>(
    'experience',
    'bold',
    { toControl: toBackgroundIntensity }
  );

  return (
    <SidebarSection label="Background" scope="For all views">
      <SidebarCard>
        <SidebarRow>
          <Flex align="center" gap={2.5} minW={0}>
            <SidebarIconChip size="26px">
              <Icon color="ink.soft" icon="Palette" size={3.5} />
            </SidebarIconChip>
            <SidebarRowLabel label="Accent background" />
          </Flex>
          <SidebarRowSwitch
            {...registerToggle('experience', 'accentBackgroundEnabled')}
          />
        </SidebarRow>
        <SidebarRow>
          <Flex align="center" gap={2.5} minW={0}>
            <SidebarIconChip size="26px">
              <Icon color="ink.soft" icon="Contrast" size={3.5} />
            </SidebarIconChip>
            <SidebarRowLabel label="Intensity" />
          </Flex>
          <IntensitySlider
            colorName={colorName}
            isDisabled={!isEnabled}
            palette={palette}
            value={value}
            onChange={onChange}
            onPreview={onPreview}
          />
        </SidebarRow>
      </SidebarCard>
    </SidebarSection>
  );
};
